/**
 * Jungle mobs (Bedrock numbers):
 *  - panda: 20 HP (weak gene 10), 1.3 x 1.25, speed 0.15. Follows and breeds with
 *    bamboo, now and then sits down to eat, drops 0-2 bamboo, 1-3 XP. Each panda
 *    carries a main and a hidden gene (normal, lazy, worried, playful, brown, weak,
 *    aggressive; brown and weak are recessive) that pick its sheet; cubs take one
 *    gene from each parent (1/32 mutation each). Hit by the player it bites back
 *    once for 6 — an aggressive one keeps at it — while worried pandas and cubs run.
 *    Not implemented: the bamboo-nearby breeding rule, rolling, sneezing, lying down.
 *  - parrot: 6 HP, 0.5 x 0.9, five colours. Flutters between perches, tamed with
 *    seeds (1/3 each), then follows its owner in the air (teleports past 12 blocks)
 *    and toggles sit / stand on a right-click. A cookie kills it. Drops 1-2
 *    feathers, 1-3 XP. Not implemented: shoulder riding, mimicry, dancing.
 *
 * `view.color` — panda: bits 0-2 visible gene, bit 3 sitting, bit 4 eating;
 * parrot: bits 0-2 variant, bit 3 tame, bit 4 sitting, bit 5 flying.
 */

import { BAMBOO_ID, IS_SOLID } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Animal } from '../animals';
import type { EntityHost } from '../host';
import { Mob, type MobCtx } from '../mob';
import { newSteer, steerFlying } from '../nav/navigator';

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

// ------------------------------------------------------------------- panda

export const PANDA_GENES = ['normal', 'lazy', 'worried', 'playful', 'brown', 'weak', 'aggressive'] as const;
const G_WORRIED = 2, G_BROWN = 4, G_WEAK = 5, G_AGGRESSIVE = 6;
export const PANDA_DAMAGE = 6;
export const PANDA_MUTATION = 1 / 32;

/** Vanilla Panda.Gene.getRandom: nextInt(16) -> lazy, worried, playful, weak x5, aggressive, brown x2, normal x5. */
export function randomPandaGene(rnd: () => number): number {
  const n = Math.floor(rnd() * 16);
  return n === 0 ? 1 : n === 1 ? G_WORRIED : n === 2 ? 3 : n === 4 ? G_AGGRESSIVE : n < 9 ? G_WEAK : n < 11 ? G_BROWN : 0;
}

/** The gene that shows: a recessive main gene (brown, weak) needs the same hidden gene. */
export function pandaVariant(main: number, hidden: number): number {
  return (main === G_BROWN || main === G_WEAK) && main !== hidden ? 0 : main;
}

export class Panda extends Animal {
  protected raw = BAMBOO_ID;
  protected cooked = BAMBOO_ID;
  mainGene = 0;
  hiddenGene = 0;
  /** Ticks left sitting down with a bamboo snack. */
  sitTicks = 0;
  private angry = 0;
  private attackCd = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'panda', x, y, z, { width: 1.3, height: 1.25, eye: 1, health: 20, speed: 0.15 }, rnd);
    this.panicMod = 2;
    this.setGenes(randomPandaGene(rnd), randomPandaGene(rnd));
  }

  get gene(): number { return pandaVariant(this.mainGene, this.hiddenGene); }

  setGenes(main: number, hidden: number): void {
    const ok = (g: number): number => (g >= 0 && g < PANDA_GENES.length ? Math.floor(g) : 0);
    this.mainGene = ok(main); this.hiddenGene = ok(hidden);
    const max = this.gene === G_WEAK ? 10 : 20;
    (this as unknown as { maxHealth: number }).maxHealth = max;
    this.health = Math.min(this.health, max);
  }

  /** Vanilla Panda.setGeneFromParents: one gene from each parent, each with a 1/32 mutation. */
  override inherit(a: Animal, b: Animal, rnd: () => number): void {
    const pick = (p: Animal): number => {
      if (rnd() < PANDA_MUTATION || !(p instanceof Panda)) return randomPandaGene(rnd);
      return rnd() < 0.5 ? p.mainGene : p.hiddenGene;
    };
    this.setGenes(pick(a), pick(b));
    this.health = this.maxHealth;
  }

  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    this.sitTicks = 0;
    if (this.baby || this.gene === G_WORRIED) { super.onHurtBy(fromX, fromZ, rnd); return; }
    this.angry = 400;
  }

  protected override ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.angry > 0) {
      this.angry--;
      if (this.baby || !ctx.targetable()) this.angry = 0;
      else {
        const p = ctx.host.player;
        this.ageTick(ctx);
        this.aggressive = true;
        this.faceToward(p.x, p.z); this.lookAt(p.x, p.eyeY, p.z);
        this.steerTo(ctx, p.x, p.y, p.z, 1.6, 3);
        if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
          this.attackCd = 20;
          this.swing();
          ctx.hurtPlayer(this, PANDA_DAMAGE, 0.4);
          if (this.gene !== G_AGGRESSIVE) this.angry = 0; // everyone else settles after one bite
        }
        return;
      }
    }
    this.aggressive = false;
    if (this.sitTicks > 0) {
      if (this.panicTicks > 0 || this.mate || this.inWater) this.sitTicks = 0;
      else {
        this.sitTicks--;
        this.ageTick(ctx);
        this.lookAtNearPlayer(ctx);
        return;
      }
    } else if (!this.baby && this.onGround && this.panicTicks <= 0 && !this.mate && this.loveTicks <= 0 && ctx.host.rnd() < 1 / 600) {
      this.sitTicks = 100 + Math.floor(ctx.host.rnd() * 200);
      return;
    }
    super.ai(ctx);
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    // settles for half a second before the bamboo comes out
    this.view.color = this.gene | (this.sitTicks > 0 ? 8 : 0) | (this.sitTicks > 10 ? 16 : 0);
  }

  protected override saveMore(o: Record<string, number>): void { o.pm = this.mainGene; o.ph = this.hiddenGene; }
  protected override loadMore(o: Record<string, number>): void {
    if (typeof o.pm === 'number') this.setGenes(o.pm, o.ph ?? 0);
  }

  loot(rnd: () => number): Stack[] {
    if (this.noDrops()) return [];
    const n = roll(rnd, 0, 2);
    return n > 0 ? [stackOf(BAMBOO_ID, n)] : [];
  }
}

// ------------------------------------------------------------------ parrot

export const PARROT_VARIANTS = ['red_blue', 'blue', 'green', 'yellow_blue', 'grey'] as const;
export const PARROT_TAME_CHANCE = 1 / 3;
export const PARROT_TELEPORT_DIST = 12;
const SEEDS: ReadonlySet<number> = new Set(
  ['WHEAT_SEEDS', 'BEETROOT_SEEDS', 'MELON_SEEDS', 'PUMPKIN_SEEDS', 'TORCHFLOWER_SEEDS']
    .map((n) => (I as Record<string, number>)[n]).filter((id) => id !== undefined),
);
export const isParrotSeed = (id: number): boolean => SEEDS.has(id);
const STEER = newSteer();
const FLY_SPEED = 0.22;

export class Parrot extends Mob {
  variant: number;
  tame = false;
  sitting = false;
  /** Ticks left of the current flight; it glides down once they run out. */
  private flyTicks = 0;
  private tx = 0; private ty = 0; private tz = 0;
  private tpCd = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'parrot', x, y, z, { width: 0.5, height: 0.9, eye: 0.54, health: 6, speed: 0.2 }, rnd);
    this.noFallDamage = true;
    this.slowFall = true;
    this.variant = Math.floor(rnd() * PARROT_VARIANTS.length);
  }

  setVariant(v: number): void { this.variant = Math.max(0, Math.min(PARROT_VARIANTS.length - 1, Math.floor(v))); }

  /** Right-click with `held` (0 = empty). */
  interact(held: number, rnd: () => number): { handled: boolean; consumed: boolean } {
    if (!this.alive) return { handled: false, consumed: false };
    if (held === I.COOKIE) {
      // chocolate is poison to parrots
      this.host?.effect('poof', this.x, this.y + this.height * 0.6, this.z);
      this.hurt(1000);
      return { handled: true, consumed: true };
    }
    if (!this.tame) {
      if (!isParrotSeed(held)) return { handled: false, consumed: false };
      if (rnd() < PARROT_TAME_CHANCE) {
        this.tame = true; this.sitting = true; this.flyTicks = 0;
        this.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
      } else this.host?.effect('poof', this.x, this.y + this.height * 0.6, this.z);
      return { handled: true, consumed: true };
    }
    this.sitting = !this.sitting;
    return { handled: true, consumed: false };
  }

  override onHurtBy(): void {
    this.sitting = false;
    this.flyTicks = 0;
    this.panicTicks = 80;
  }

  xpValue(rnd: () => number): number { return roll(rnd, 1, 3); }
  loot(rnd: () => number): Stack[] { return [stackOf(I.FEATHER, roll(rnd, 1, 2))]; }

  saveExtra(): unknown { return [this.variant, this.tame ? 1 : 0, this.sitting ? 1 : 0]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.setVariant(typeof a?.[0] === 'number' ? a[0] : 0);
    this.tame = a?.[1] === 1;
    this.sitting = a?.[2] === 1;
  }

  private teleportToOwner(ctx: MobCtx): boolean {
    const p = ctx.host.player, w = this.w, rnd = ctx.host.rnd;
    for (let i = 0; i < 12; i++) {
      const tx = Math.floor(p.x + (rnd() * 6 - 3)), tz = Math.floor(p.z + (rnd() * 6 - 3));
      const ty = Math.floor(p.y) + Math.floor(rnd() * 3) - 1;
      if (Math.abs(tx + 0.5 - p.x) < 2 && Math.abs(tz + 0.5 - p.z) < 2) continue;
      if (IS_SOLID[w.getBlock(tx, ty - 1, tz)] === 1 && !IS_SOLID[w.getBlock(tx, ty, tz)] && !IS_SOLID[w.getBlock(tx, ty + 1, tz)]) {
        this.x = this.px = tx + 0.5; this.y = this.py = ty; this.z = this.pz = tz + 0.5;
        this.vx = this.vy = this.vz = 0;
        this.flyTicks = 0;
        return true;
      }
    }
    return false;
  }

  /** One tick of powered flight toward (tx, ty, tz); the velocity is set outright, so gravity never builds up. */
  private flyToward(tx: number, ty: number, tz: number): void {
    steerFlying(this.w, this.x, this.y, this.z, tx, ty, tz, 2, STEER);
    this.vx += (STEER.dx * FLY_SPEED - this.vx) * 0.25;
    this.vz += (STEER.dz * FLY_SPEED - this.vz) * 0.25;
    this.vy = STEER.dy * FLY_SPEED;
    this.faceToward(this.x + STEER.dx, this.z + STEER.dz);
  }

  private takeOff(ctx: MobCtx, far: number): void {
    const r = ctx.host.rnd;
    this.tx = this.x + (r() * 2 - 1) * far;
    this.tz = this.z + (r() * 2 - 1) * far;
    this.ty = this.y + 1 + r() * 4;
    this.flyTicks = 40 + Math.floor(r() * 80);
    this.vy = 0.2;
  }

  protected ai(ctx: MobCtx): void {
    if (this.tpCd > 0) this.tpCd--;
    if (this.panicTicks > 0) this.panicTicks--;
    const p = ctx.host.player;
    if (this.tame) {
      if (this.sitting) { this.flyTicks = 0; this.lookAtNearPlayer(ctx); return; }
      const dx = p.x - this.x, dz = p.z - this.z, d2 = dx * dx + dz * dz;
      if (d2 > PARROT_TELEPORT_DIST * PARROT_TELEPORT_DIST && this.tpCd === 0) { this.tpCd = 20; if (this.teleportToOwner(ctx)) return; }
      if (d2 > 25) {
        // FollowOwnerGoal, on the wing
        this.flyTicks = 0;
        this.flyToward(p.x, p.y + 1.6, p.z);
        this.lookAt(p.x, p.eyeY, p.z);
        return;
      }
    }
    if (this.flyTicks > 0) {
      this.flyTicks--;
      this.flyToward(this.tx, this.ty, this.tz);
      if (this.dist2To(this.tx, this.ty, this.tz) < 1.5 || this.inWater) this.flyTicks = 0;
      return;
    }
    if (!this.onGround) return; // gliding down to a perch
    if (this.panicTicks > 0) { this.takeOff(ctx, 10); return; }
    if (ctx.host.rnd() < 1 / 160) { this.takeOff(ctx, 7); return; }
    this.stroll(ctx, 1, 3, true);
    this.lookAtNearPlayer(ctx);
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.color = this.variant | (this.tame ? 8 : 0) | (this.sitting ? 16 : 0) | (!this.onGround && !this.inWater ? 32 : 0);
  }
}

// ---------------------------------------------------------------- spawning

export const JUNGLE_MOB_TYPES: readonly MobType[] = ['panda', 'parrot'];

/**
 * Jungle chunks that rolled a herd: pandas keep to bamboo jungle (and rarely the
 * plain jungle), parrots flock in every jungle but the bamboo thickets less so.
 * Null leaves the chunk to the ordinary farm animals.
 */
export function jungleHerd(roll: number, sizeRoll: number, biome: string): { type: MobType; count: number } | null {
  if (!biome.includes('jungle')) return null;
  const count = 1 + Math.floor(sizeRoll * 2);
  if (biome.includes('bamboo')) return roll < 0.5 ? { type: 'panda', count } : roll < 0.75 ? { type: 'parrot', count } : null;
  if (biome.includes('sparse')) return roll < 0.35 ? { type: 'parrot', count } : null;
  return roll < 0.08 ? { type: 'panda', count } : roll < 0.6 ? { type: 'parrot', count } : null;
}
